import * as Tone from 'tone';
import { perfVariant } from './perfVariant';

// Instrumentation de diagnostic du lecteur de leçons. Inactive par défaut :
// chaque fonction sort sur le test `enabled` avant de toucher à quoi que ce soit.
// Activation : localStorage 'upscale_perf' = '1', ou window.__upscalePerf.start().

const DEFAULT_CAPACITY = 20000;
// Au-delà, la marque d'entrée ne correspond plus au geste qui suit (ex : une
// flèche gauche en début de section ne joue aucun pas, la marque resterait collée).
const INPUT_MARK_TTL = 500;

let enabled = false;
let capacity = DEFAULT_CAPACITY;
let buffer = new Array(capacity);
let head = 0;
let count = 0;
let tracedFrom = 0;
let inputMark = null;
let observer = null;
let frameRaf = null;
let lastFrameAt = 0;

const now = () => performance.now();

const push = (event) => {
  buffer[head] = event;
  head = (head + 1) % capacity;
  if (count < capacity) count += 1;
};

const allEvents = () => {
  if (count < capacity) return buffer.slice(0, count);
  return buffer.slice(head, capacity).concat(buffer.slice(0, head));
};

const clearBuffer = (size) => {
  capacity = Number.isFinite(size) && size > 0 ? Math.floor(size) : capacity;
  buffer = new Array(capacity);
  head = 0;
  count = 0;
  inputMark = null;
};

// Tone est mocké dans les tests et le contexte natif n'expose pas partout
// getOutputTimestamp : tout est optionnel, rien ne doit jeter.
const readContext = () => {
  const out = {};
  try {
    if (typeof Tone.getContext !== 'function') return out;
    const ctx = Tone.getContext();
    if (!ctx) return out;
    if (ctx.state) out.ctxState = ctx.state;
    // lookAhead de Tone, pas celui du contexte natif.
    if (typeof ctx.lookAhead === 'number') out.toneLookAhead = ctx.lookAhead;
    if (typeof ctx.sampleRate === 'number') out.sampleRate = ctx.sampleRate;
    const raw = ctx.rawContext;
    if (!raw) return out;
    if (typeof raw.sampleRate === 'number') out.sampleRate = raw.sampleRate;
    if (typeof raw.baseLatency === 'number') out.baseLatency = raw.baseLatency;
    if (typeof raw.outputLatency === 'number') out.outputLatency = raw.outputLatency;
    if (typeof raw.currentTime === 'number') out.rawCurrentTime = raw.currentTime;
    if (typeof raw.getOutputTimestamp === 'function') {
      const ts = raw.getOutputTimestamp();
      if (ts) {
        out.outContextTime = ts.contextTime;
        out.outPerformanceTime = ts.performanceTime;
      }
    }
  } catch (e) {
    // contexte audio indisponible, on trace sans
  }
  return out;
};

const startObserver = () => {
  if (observer || typeof PerformanceObserver !== 'function') return;
  try {
    observer = new PerformanceObserver((list) => {
      if (!enabled) return;
      list.getEntries().forEach((entry) => {
        push({ t: now(), type: 'longtask', duration: entry.duration });
      });
    });
    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    observer = null;
  }
};

const stopObserver = () => {
  if (!observer) return;
  try {
    observer.disconnect();
  } catch (e) {
    // rien à faire
  }
  observer = null;
};

const stopFrames = () => {
  if (frameRaf == null) return;
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRaf);
  frameRaf = null;
  lastFrameAt = 0;
};

export const isEnabled = () => enabled;

export const start = (options) => {
  const size = options && options.capacity;
  if (Number.isFinite(size) && size > 0) clearBuffer(size);
  enabled = true;
  tracedFrom = now();
  startObserver();
};

export const stop = () => {
  enabled = false;
  stopObserver();
  stopFrames();
};

export const reset = () => {
  clearBuffer();
  tracedFrom = now();
};

export const traceSchedule = (info) => {
  if (!enabled || !info) return;
  const perfNow = now();
  const event = {
    t: perfNow,
    type: 'schedule',
    perfNow,
    mode: info.mode,
    stepIndex: info.stepIndex,
    musicalTime: info.musicalTime,
    when: info.when,
    audioNow: info.audioNow,
    bps: info.bps,
    lateness: info.audioNow - info.when,
    ...readContext()
  };
  // dueIn : l'échéance non bornée. Un pas rattrapé est poussé par le moteur à
  // `now` + 5 ms, donc un retard réel n'apparaît que là.
  if (Number.isFinite(info.dueIn)) event.dueIn = info.dueIn;
  if (inputMark != null) {
    if (info.mode === 'manual' && perfNow - inputMark <= INPUT_MARK_TTL) {
      event.inputToSchedule = perfNow - inputMark;
    }
    inputMark = null;
  }
  push(event);
};

// Pas sauté : trop en retard pour être déclenché sans rafale.
export const traceSkip = (info) => {
  if (!enabled || !info) return;
  push({ t: now(), type: 'skip', stepIndex: info.stepIndex, lateBy: info.lateBy });
};

export const traceInput = (ts) => {
  if (!enabled) return;
  const perfNow = now();
  // Un timeStamp hors de la base performance.now() (vieux WebKit, base epoch)
  // donnerait un écart absurde : on retombe alors sur l'horloge du module.
  const usable = Number.isFinite(ts) && ts <= perfNow && perfNow - ts < INPUT_MARK_TTL * 10;
  inputMark = usable ? ts : perfNow;
  push({ t: perfNow, type: 'input', ts: inputMark });
};

export const traceStepCommit = (stepIndex) => {
  if (!enabled) return;
  push({ t: now(), type: 'stepCommit', stepIndex });
};

export const traceRender = (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
  if (!enabled) return;
  push({ t: now(), type: 'render', id, phase, actualDuration, baseDuration, startTime, commitTime });
};

// Prête à passer telle quelle en prop onRender d'un <Profiler>.
export const onRenderProfile = traceRender;

export const traceFrames = (on) => {
  if (!on) {
    stopFrames();
    return;
  }
  if (!enabled || frameRaf != null) return;
  if (typeof requestAnimationFrame !== 'function') return;
  lastFrameAt = 0;
  const loop = () => {
    const at = now();
    if (lastFrameAt) push({ t: at, type: 'frame', dt: at - lastFrameAt });
    lastFrameAt = at;
    frameRaf = requestAnimationFrame(loop);
  };
  frameRaf = requestAnimationFrame(loop);
};

const percentile = (sorted, p) => {
  if (!sorted.length) return null;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[idx];
};

const stats = (values) => {
  const clean = values.filter((v) => Number.isFinite(v));
  if (!clean.length) return { count: 0, p50: null, p95: null, max: null };
  const sorted = clean.slice().sort((a, b) => a - b);
  return {
    count: sorted.length,
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    max: sorted[sorted.length - 1]
  };
};

const buildSummary = (events) => {
  const latenessAuto = [];
  const latenessManual = [];
  const dueInAuto = [];
  const inputToSchedule = [];
  const scheduleIntervalError = [];
  const frameDt = [];
  const longtask = [];
  const skipped = [];
  const renderById = {};
  const commitsById = {};
  const pendingCommits = {};

  const flushCommits = () => {
    Object.keys(pendingCommits).forEach((id) => {
      if (!commitsById[id]) commitsById[id] = [];
      commitsById[id].push(pendingCommits[id]);
      delete pendingCommits[id];
    });
  };

  let prevAuto = null;
  events.forEach((e) => {
    if (e.type === 'schedule') {
      if (e.mode === 'manual') {
        latenessManual.push(e.lateness);
        if (Number.isFinite(e.inputToSchedule)) inputToSchedule.push(e.inputToSchedule);
      } else {
        latenessAuto.push(e.lateness);
        if (Number.isFinite(e.dueIn)) dueInAuto.push(e.dueIn);
        // Écart entre l'intervalle audio programmé et l'intervalle musical attendu.
        // Seulement sur deux pas consécutifs : un retour de boucle ou un seek
        // ferait un intervalle musical négatif et polluerait la mesure.
        if (prevAuto && e.stepIndex === prevAuto.stepIndex + 1
            && Number.isFinite(prevAuto.bps) && prevAuto.bps > 0) {
          const expected = ((e.musicalTime - prevAuto.musicalTime) / prevAuto.bps) * 1000;
          const actual = (e.when - prevAuto.when) * 1000;
          if (Number.isFinite(expected) && Number.isFinite(actual)) {
            scheduleIntervalError.push(Math.abs(actual - expected));
          }
        }
        prevAuto = e;
      }
    } else if (e.type === 'skip') {
      skipped.push(e.lateBy);
    } else if (e.type === 'frame') {
      frameDt.push(e.dt);
    } else if (e.type === 'longtask') {
      longtask.push(e.duration);
    } else if (e.type === 'render') {
      if (!renderById[e.id]) renderById[e.id] = [];
      renderById[e.id].push(e.actualDuration);
      pendingCommits[e.id] = (pendingCommits[e.id] || 0) + 1;
    } else if (e.type === 'stepCommit') {
      flushCommits();
    }
  });
  flushCommits();

  const render = {};
  Object.keys(renderById).forEach((id) => { render[id] = stats(renderById[id]); });
  const commitsPerStep = {};
  Object.keys(commitsById).forEach((id) => { commitsPerStep[id] = stats(commitsById[id]); });

  return {
    latenessAuto: stats(latenessAuto),
    latenessManual: stats(latenessManual),
    dueInAuto: stats(dueInAuto),
    inputToSchedule: stats(inputToSchedule),
    scheduleIntervalError: stats(scheduleIntervalError),
    skipped: stats(skipped),
    frameDt: stats(frameDt),
    longtask: stats(longtask),
    render,
    commitsPerStep
  };
};

const buildMeta = () => {
  const ctx = readContext();
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const screenOrientation = (typeof window !== 'undefined' && window.screen && window.screen.orientation)
    ? window.screen.orientation.type
    : null;
  let toneVersion = null;
  try {
    toneVersion = Tone.version || null;
  } catch (e) {
    toneVersion = null;
  }
  return {
    date: new Date().toISOString(),
    durationMs: now() - tracedFrom,
    userAgent: nav ? nav.userAgent : null,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
    innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
    innerHeight: typeof window !== 'undefined' ? window.innerHeight : null,
    orientation: screenOrientation,
    toneVersion,
    variant: perfVariant(),
    sampleRate: ctx.sampleRate ?? null,
    lookAhead: ctx.toneLookAhead ?? null,
    baseLatency: ctx.baseLatency ?? null,
    outputLatency: ctx.outputLatency ?? null,
    capacity,
    dropped: count >= capacity
  };
};

export const exportTrace = () => {
  const events = allEvents();
  return { meta: buildMeta(), events, summary: buildSummary(events) };
};

if (typeof window !== 'undefined') {
  window.__upscalePerf = {
    start,
    stop,
    reset,
    export: exportTrace,
    isEnabled
  };
  try {
    if (window.localStorage && window.localStorage.getItem('upscale_perf') === '1') start();
  } catch (e) {
    // localStorage inaccessible (mode privé, iframe), on reste inactif
  }
}
